"use client"

/**
 * Code Editor panel for use inside flexlayout-react tabs.
 *
 * Collaborative mode: when `MonacoBinding` attaches, the Monaco model is driven by
 * `Y.Text` (no React `value` — avoids fighting the binding). Otherwise uses the
 * legacy React `value` + `onChange` path (`updateContent` → `Y.Text`).
 *
 * Binding lifecycle is driven deterministically by `useLayoutEffect` on
 * `sessionId`, `fileId`, `crdtProviderEpoch`, `crdtMapRevision`, and `editorMountNonce`
 * (no arbitrary setTimeout). `editorMountNonce` bumps after Monaco `onMount` because
 * the editor ref is only available then (Monaco loads asynchronously).
 *
 * Tab management (close, reorder, drag) is handled by flexlayout.
 */

import { useState, useCallback, useRef, useEffect, useLayoutEffect } from "react"
import Editor, { type OnMount, type BeforeMount } from "@monaco-editor/react"
import type { editor, IDisposable } from "monaco-editor"
import type { TabNode } from "flexlayout-react"
import { useThemeMode } from "@/lib/themes/theme-mode"
import type { MonacoBinding } from "y-monaco"
import * as Y from "yjs"
import type { Awareness } from "y-protocols/awareness"

import { CollaboratorCursors } from "@/components/editor/collaborator-cursors"
import { attachMonacoAwarenessSelectionSync } from "@/lib/crdt/monaco-awareness"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { cn } from "@/lib/utils"
import type { AwarenessUserState } from "@/lib/crdt/awareness-user"

import {
  registerLatexLanguage,
  attachBeginEndMatching,
  attachDiagnostics,
  LATEX_LANGUAGE_ID,
} from "@/lib/monaco-latex"
import { debounce } from "@/lib/debounce"
import { useEditorStore, resolveEditorFilePath, type SynctexForwardBox } from "@/lib/editor-store"
import { isImageFile, normalizePath } from "@/lib/compiler/path-utils"
import { hasAuthSession } from "@/lib/auth-client"
import { isProjectSettingsPath } from "@/lib/project-settings"
import { registerProjectSettingsJsonSchema } from "@/lib/project-settings-monaco"
import { ImagePreviewPanel } from "./image-preview-panel"

const BASE_EDITOR_FONT_SIZE = 14

let monacoBindingCtorPromise: Promise<typeof import("y-monaco").MonacoBinding> | null = null

function loadMonacoBindingCtor(): Promise<typeof import("y-monaco").MonacoBinding> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("MonacoBinding is browser-only"))
  }
  if (!monacoBindingCtorPromise) {
    monacoBindingCtorPromise = import("y-monaco").then((mod) => mod.MonacoBinding)
  }
  return monacoBindingCtorPromise
}

function readAwarenessUser(raw: unknown): AwarenessUserState | null {
  if (!raw || typeof raw !== "object") return null
  const user = raw as Record<string, unknown>
  const name = typeof user.name === "string" ? user.name : ""
  const color = typeof user.color === "string" ? user.color : "#8B5CF6"
  if (!name) return null
  return {
    name,
    color,
    colorLight: typeof user.colorLight === "string" ? user.colorLight : "rgba(139, 92, 246, 0.2)",
    initials: typeof user.initials === "string" ? user.initials : name.slice(0, 1).toUpperCase(),
    username: typeof user.username === "string" ? user.username : undefined,
    avatarUrl: typeof user.avatarUrl === "string" ? user.avatarUrl : undefined,
  }
}

function useFilePresenceUsers(
  getYjsBinding: ReturnType<typeof useEditorStore>["getYjsBinding"],
  filePath: string,
  revision: number,
) {
  const [users, setUsers] = useState<AwarenessUserState[]>([])

  useEffect(() => {
    const binding = getYjsBinding()
    if (!binding?.awareness) {
      setUsers([])
      return
    }

    const { awareness } = binding
    const normalized = normalizePath(filePath)

    const update = () => {
      const next: AwarenessUserState[] = []
      const seen = new Set<string>()
      awareness.getStates().forEach((rawState) => {
        const state = rawState as { user?: unknown; activeFile?: string | null }
        const user = readAwarenessUser(state.user)
        if (!user) return
        const remoteFile = state.activeFile ? normalizePath(state.activeFile) : null
        // Include peers on this file, and anyone without an activeFile yet (just joined).
        if (remoteFile && remoteFile !== normalized) return
        const key = user.username || user.name
        if (seen.has(key)) return
        seen.add(key)
        next.push(user)
      })
      setUsers(next.slice(0, 5))
    }

    update()
    awareness.on("update", update)
    return () => {
      awareness.off("update", update)
    }
  }, [getYjsBinding, filePath, revision])

  return users
}

function parseSynctexForwardJson(data: unknown): SynctexForwardBox | null {
  if (!data || typeof data !== "object") return null
  const o = data as Record<string, unknown>
  // Backend returns found:false when no synctex entry matches the source location
  if (o.found === false) return null
  const page = Number(o.page)
  const x = Number(o.x)
  const y = Number(o.y)
  const width = Number(o.width)
  const height = Number(o.height)
  if (!Number.isFinite(page) || page < 1) return null
  if (![x, y, width, height].every(Number.isFinite)) return null
  return { page: Math.trunc(page), x, y, width, height }
}

interface CodeEditorPanelProps {
  node: TabNode
  fileId?: string
}

type CollabEditorMode = "bound" | "fallback"

export function CodeEditorPanel({ node, fileId = "1" }: CodeEditorPanelProps) {
  const { theme: appTheme } = useThemeMode()
  const { getContent, updateContent, state, applySynctexForwardResult, postToFlutterFrame, registerInverseSyncHandler, registerActiveMonacoEditor, registerEditorHistoryHandlers, undoActiveEditor, redoActiveEditor, getYjsBinding, setActiveFile } = useEditorStore()
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)
  const featureDisposables = useRef<IDisposable[]>([])
  const monacoBindingRef = useRef<MonacoBinding | null>(null)
  const yUndoManagerRef = useRef<Y.UndoManager | null>(null)
  const awarenessSelectionDisposableRef = useRef<IDisposable | null>(null)
  const lineCountDisposableRef = useRef<IDisposable | null>(null)
  /** Unsub when waiting for empty Y.Text stub to receive peer/local content before binding. */
  const emptyYtextWaitUnsubRef = useRef<(() => void) | null>(null)
  const syncCollabBindingRef = useRef<() => void>(() => {})
  const collabModeRef = useRef<CollabEditorMode>("fallback")
  const collabYtextRef = useRef<Y.Text | null>(null)

  const [cursorLine, setCursorLine] = useState(1)
  const [totalLines, setTotalLines] = useState(100)
  const [zoomPercent, setZoomPercent] = useState(100)
  const [collabMode, setCollabMode] = useState<CollabEditorMode>("fallback")
  const [editorMountNonce, setEditorMountNonce] = useState(0)
  const [activeEditor, setActiveEditor] = useState<editor.IStandaloneCodeEditor | null>(null)
  const [collabBinding, setCollabBinding] = useState<{
    awareness: Awareness
    doc: Y.Doc
    ytext: Y.Text
  } | null>(null)
  const [synctexError, setSynctexError] = useState<string | null>(null)
  const [activeModelPath, setActiveModelPath] = useState<string | null>(null)

  const content = getContent(fileId) || `% Empty file: ${node.getName()}`
  const currentFile = state.files[fileId]
  const readOnly = Boolean(currentFile?.isAsset)

  const expectedPath = resolveEditorFilePath(fileId)
  const isModelReady = activeModelPath === normalizePath(expectedPath)
  const showFallback = collabMode === "fallback" && isModelReady

  const fileIdRef = useRef(fileId)
  const readOnlyRef = useRef(readOnly)
  const sessionIdRef = useRef(state.sessionId)
  const pdfUrlRef = useRef(state.pdfUrl)
  const applySynctexRef = useRef(applySynctexForwardResult)
  const postToFrameRef = useRef(postToFlutterFrame)

  // Update refs synchronously in render
  fileIdRef.current = fileId
  readOnlyRef.current = readOnly
  sessionIdRef.current = state.sessionId
  pdfUrlRef.current = state.pdfUrl
  applySynctexRef.current = applySynctexForwardResult
  postToFrameRef.current = postToFlutterFrame

  const syncVersionRef = useRef(0)
  const bindGenerationRef = useRef(0)
  const synctexDebounceRef = useRef<((line: number, column: number, occurrenceIndex: number) => void) & { cancel: () => void } | null>(null)
  const isInverseSyncingRef = useRef(false)
  const inverseSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const destroyMonacoBinding = useCallback(() => {
    bindGenerationRef.current += 1
    emptyYtextWaitUnsubRef.current?.()
    emptyYtextWaitUnsubRef.current = null
    awarenessSelectionDisposableRef.current?.dispose()
    awarenessSelectionDisposableRef.current = null
    collabYtextRef.current = null
    if (yUndoManagerRef.current) {
      yUndoManagerRef.current.destroy()
      yUndoManagerRef.current = null
    }
    registerEditorHistoryHandlers(null)
    if (monacoBindingRef.current) {
      try {
        monacoBindingRef.current.destroy()
      } catch {
        /* Monaco model may already be disposed */
      }
      monacoBindingRef.current = null
    }
  }, [registerEditorHistoryHandlers])

  const disposeLineCountListener = useCallback(() => {
    lineCountDisposableRef.current?.dispose()
    lineCountDisposableRef.current = null
  }, [])

  const syncCollabBinding = useCallback(() => {
    const setFallback = () => {
      collabYtextRef.current = null
      setCollabBinding(null)
      collabModeRef.current = "fallback"
      setCollabMode("fallback")
    }

    if (readOnlyRef.current) {
      destroyMonacoBinding()
      setFallback()
      return
    }

    const ed = editorRef.current
    if (!ed) {
      destroyMonacoBinding()
      setFallback()
      return
    }

    const model = ed.getModel()
    if (!model || model.isDisposed()) {
      destroyMonacoBinding()
      setFallback()
      return
    }

    // Verify the editor model matches the expected fileId to prevent binding to the wrong file during transitions
    const expectedPath = resolveEditorFilePath(fileIdRef.current)
    const modelPath = model.uri.path.replace(/^\//, "")
    if (normalizePath(modelPath) !== normalizePath(expectedPath)) {
      return
    }

    const binding = getYjsBinding()
    if (!binding) {
      destroyMonacoBinding()
      setFallback()
      return
    }

    const path = resolveEditorFilePath(fileIdRef.current)
    const ytext = binding.getYText(path)
    // A deleted file id is removed from the content map and getYText returns null.
    // Destroy the binding so Monaco cannot keep editing that Y.Text.
    if (!ytext || ytext.doc !== binding.doc) {
      destroyMonacoBinding()
      setFallback()
      return
    }

    // Already bound to the live map entry — avoid destroy/recreate (drops peer cursors).
    if (
      monacoBindingRef.current &&
      collabModeRef.current === "bound" &&
      collabYtextRef.current === ytext
    ) {
      // Re-bind history handlers in case a prior destroy cleared them without recreating the binding
      const um = yUndoManagerRef.current
      if (um) {
        registerEditorHistoryHandlers({
          undo: () => um.undo(),
          redo: () => um.redo(),
          canUndo: () => um.canUndo(),
          canRedo: () => um.canRedo(),
        })
      }
      return
    }

    // Empty CRDT stubs are common on open. Binding Monaco to them hides hydrated React
    // content and can race with peers. Stay on React fallback until Y.Text has content.
    const yStr = ytext.toString()
    const reactContent = getContent(fileIdRef.current)
    const fileName = path.split("/").pop() || path
    const reactHasRealContent =
      Boolean(reactContent?.trim()) && reactContent !== `% ${fileName}\n`
    if (!yStr.trim() && reactHasRealContent) {
      bindGenerationRef.current += 1
      emptyYtextWaitUnsubRef.current?.()
      emptyYtextWaitUnsubRef.current = null
      if (monacoBindingRef.current) {
        try {
          monacoBindingRef.current.destroy()
        } catch {
          /* ignore */
        }
        monacoBindingRef.current = null
      }
      if (yUndoManagerRef.current) {
        yUndoManagerRef.current.destroy()
        yUndoManagerRef.current = null
      }
      registerEditorHistoryHandlers(null)
      awarenessSelectionDisposableRef.current?.dispose()
      awarenessSelectionDisposableRef.current = null
      collabYtextRef.current = null
      setCollabBinding(null)
      collabModeRef.current = "fallback"
      setCollabMode("fallback")
      const onYtext = () => {
        if (!ytext.toString().trim()) return
        emptyYtextWaitUnsubRef.current?.()
        emptyYtextWaitUnsubRef.current = null
        syncCollabBindingRef.current()
      }
      ytext.observe(onYtext)
      emptyYtextWaitUnsubRef.current = () => {
        ytext.unobserve(onYtext)
      }
      return
    }

    destroyMonacoBinding()

    const bindGeneration = ++bindGenerationRef.current
    void loadMonacoBindingCtor()
      .then((MonacoBinding) => {
        if (bindGeneration !== bindGenerationRef.current) return
        if (editorRef.current !== ed) return
        const liveModel = ed.getModel()
        if (!liveModel || liveModel.isDisposed()) return

        monacoBindingRef.current = new MonacoBinding(ytext, liveModel, new Set([ed]), binding.awareness)
        // Track MonacoBinding instance + class so all binding-originated edits are undoable
        const um = new Y.UndoManager(ytext, {
          trackedOrigins: new Set([monacoBindingRef.current, MonacoBinding, null]),
          captureTimeout: 500,
        })
        yUndoManagerRef.current = um
        registerEditorHistoryHandlers({
          undo: () => {
            um.undo()
          },
          redo: () => {
            um.redo()
          },
          canUndo: () => um.canUndo(),
          canRedo: () => um.canRedo(),
        })
        awarenessSelectionDisposableRef.current = attachMonacoAwarenessSelectionSync(
          ed,
          liveModel,
          binding.awareness,
          path,
        )
        collabYtextRef.current = ytext
        setCollabBinding({ awareness: binding.awareness, doc: binding.doc, ytext })
        collabModeRef.current = "bound"
        setCollabMode("bound")
        setActiveFile(path)

        console.log("[Cursors] MonacoBinding attached", {
          path,
          sessionId: sessionIdRef.current,
          yLen: ytext.length,
          modelLen: liveModel.getValue().length,
          awarenessPeers: binding.awareness.getStates().size - 1,
          hasLocalSelection: Boolean(
            (binding.awareness.getLocalState() as { selection?: unknown } | null)?.selection,
          ),
        })
        if (liveModel.getValue() !== ytext.toString()) {
          console.warn("[Cursors] Monaco model still differs from Y.Text after bind", {
            path,
            modelLen: liveModel.getValue().length,
            yLen: ytext.length,
          })
        }
      })
      .catch((err) => {
        if (bindGeneration !== bindGenerationRef.current) return
        console.warn("[Cursors] MonacoBinding attach failed", err)
        destroyMonacoBinding()
        collabYtextRef.current = null
        setCollabBinding(null)
        collabModeRef.current = "fallback"
        setCollabMode("fallback")
      })
  }, [getYjsBinding, destroyMonacoBinding, setActiveFile, getContent, registerEditorHistoryHandlers])

  syncCollabBindingRef.current = syncCollabBinding

  useLayoutEffect(() => {
    syncCollabBinding()
    return () => {
      destroyMonacoBinding()
      collabYtextRef.current = null
      setCollabBinding(null)
      collabModeRef.current = "fallback"
      setCollabMode("fallback")
    }
  }, [
    state.sessionId,
    fileId,
    state.crdtProviderEpoch,
    state.crdtMapRevision,
    readOnly,
    editorMountNonce,
    syncCollabBinding,
    destroyMonacoBinding,
  ])

  // Typing indicator: broadcast isTyping over CRDT awareness so peers see live "typing" dots.
  // MonacoBinding also drives onDidChangeModelContent when it applies a *remote* peer's edit
  // to this model, so a naive listener would flag every connected client as "typing" whenever
  // anyone types. Probe MonacoBinding's own mux (exposed on the instance) — it only lets our
  // callback run when we're not already nested inside the binding's remote-apply lock, which
  // is exactly how y-monaco distinguishes local edits from remote ones internally.
  useEffect(() => {
    if (!activeEditor || !collabBinding) return
    const { awareness } = collabBinding
    let typingTimeout: ReturnType<typeof setTimeout> | null = null

    const disposable = activeEditor.onDidChangeModelContent(() => {
      let isLocalEdit = false
      monacoBindingRef.current?.mux(() => {
        isLocalEdit = true
      })
      if (!isLocalEdit) return

      awareness.setLocalStateField("isTyping", true)
      if (typingTimeout) clearTimeout(typingTimeout)
      typingTimeout = setTimeout(() => {
        awareness.setLocalStateField("isTyping", false)
      }, 1200)
    })

    return () => {
      disposable.dispose()
      if (typingTimeout) clearTimeout(typingTimeout)
      awareness.setLocalStateField("isTyping", false)
    }
  }, [activeEditor, collabBinding])

  const handleBeforeMount: BeforeMount = useCallback((monaco) => {
    registerLatexLanguage(monaco)
    registerProjectSettingsJsonSchema(monaco)
  }, [])

  const handleEditorMount: OnMount = useCallback(
    (editor, monaco) => {
      editorRef.current = editor
      setActiveEditor(editor)
      registerActiveMonacoEditor(editor)

      // Override Ctrl/Cmd+Z / Shift+Z / Y so collab Y.UndoManager is used when bound
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyZ, () => {
        undoActiveEditor()
      })
      editor.addCommand(
        monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyZ,
        () => {
          redoActiveEditor()
        },
      )
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyY, () => {
        redoActiveEditor()
      })

      for (const d of featureDisposables.current) d.dispose()
      featureDisposables.current = []

      disposeLineCountListener()

      const model = editor.getModel()
      if (model) {
        setActiveModelPath(normalizePath(model.uri.path.replace(/^\//, "")))
        setTotalLines(model.getLineCount())
        lineCountDisposableRef.current = model.onDidChangeContent(() => {
          setTotalLines(model.getLineCount())
        })
      }

      // Track active model and trigger re-binding when it changes
      const modelChangeDisposable = editor.onDidChangeModel(() => {
        const nextModel = editor.getModel()
        if (nextModel) {
          setActiveModelPath(normalizePath(nextModel.uri.path.replace(/^\//, "")))
          setTotalLines(nextModel.getLineCount())
          disposeLineCountListener()
          lineCountDisposableRef.current = nextModel.onDidChangeContent(() => {
            setTotalLines(nextModel.getLineCount())
          })
        } else {
          setActiveModelPath(null)
          disposeLineCountListener()
        }
        setEditorMountNonce((n) => n + 1)
      })
      featureDisposables.current.push(modelChangeDisposable)

      synctexDebounceRef.current?.cancel()
      // col passed in is Monaco's 1-based column; backend expects 0-based col.
      synctexDebounceRef.current = debounce((line: number, col1based: number, occurrenceIndex: number) => {
        void (async () => {
          const sessionId = sessionIdRef.current
          const pdfUrl = pdfUrlRef.current
          if (!sessionId || !pdfUrl || readOnlyRef.current) return
          const texPath = resolveEditorFilePath(fileIdRef.current)
          if (!texPath.toLowerCase().endsWith(".tex")) return
          const version = ++syncVersionRef.current
          const col0based = Math.max(0, col1based - 1)
          try {
            const res = await fetch(
              `/api/compiler/sessions/${encodeURIComponent(sessionId)}/synctex/forward?file=${encodeURIComponent(texPath)}&line=${line}&col=${col0based}&occurrence=${occurrenceIndex}`,
            )
            // Drop result if a newer selection already started
            if (version !== syncVersionRef.current) return
            if (!res.ok) {
              let msg = `SyncTeX forward failed (${res.status})`
              try {
                const err = await res.json() as Record<string, unknown>
                if (typeof err.error === "string") msg = err.error
                else if (typeof err.message === "string") msg = err.message
              } catch { /* ignore parse error */ }
              setSynctexError(msg)
              return
            }
            setSynctexError(null)
            const data: unknown = await res.json()
            const box = parseSynctexForwardJson(data)
            console.log("[SyncTeX:Debug] Parsed forward box:", box)
            if (box) {
              const payload = {
                ...box,
                sourceLine: line,
                sourceColumn: col1based,
                occurrenceIndex,
              }
              console.log("[SyncTeX:Debug] Calling applySynctexRef with:", payload)
              applySynctexRef.current(payload)
            }
          } catch {
            setSynctexError("SyncTeX forward: network error")
          }
        })()
      }, 300)

      editor.onDidChangeCursorSelection((e) => {
        if (isInverseSyncingRef.current) return

        const sel = e.selection
        const line = sel.positionLineNumber
        const col = sel.positionColumn
        setCursorLine(line)

        postToFrameRef.current({ type: "SET_CURSOR", line, column: col })

        const model = editor.getModel()
        if (!model) return

        const selText = model.getValueInRange(sel).trim()
        console.log("[SyncTeX:Debug] onDidChangeCursorSelection:", {
          line,
          col,
          selText,
          isSelectionEmpty: sel.isEmpty(),
        })
        if (selText) {
          postToFrameRef.current({ type: "SET_SELECTED_WORD", word: selText })

          const startLine = sel.startLineNumber
          const startCol = sel.startColumn

          const fullContent = model.getValue()
          const cursorOffset = model.getOffsetAt({ lineNumber: startLine, column: startCol })
          const textBefore = fullContent.substring(0, cursorOffset)

          const escaped = selText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
          const occurrenceIndex = (textBefore.match(new RegExp(escaped, "g")) ?? []).length

          console.log("[SyncTeX:Debug] triggering synctexDebounce:", {
            startLine,
            startCol,
            occurrenceIndex,
            word: selText,
          })

          synctexDebounceRef.current?.(startLine, startCol, occurrenceIndex)
        }else {
          // No selection — cancel in-flight sync and clear Flutter highlight
          synctexDebounceRef.current?.cancel()
          syncVersionRef.current++
          postToFrameRef.current({ type: "SET_SELECTED_WORD", word: "" })
        }
      })

      // Inverse sync: PDF click → move editor cursor to the returned position and select the word
      registerInverseSyncHandler((line, column, word) => {
        isInverseSyncingRef.current = true
        try {
          const model = editor.getModel()
          let targetColumn = column
          let foundSelection = null

          if (model) {
            const lineText = model.getLineContent(line)
            if (word) {
              // Find all occurrences of word in lineText and select the one closest to column
              let bestIndex = -1
              let minDiff = Infinity
              let index = lineText.indexOf(word)
              while (index !== -1) {
                const col = index + 1
                const diff = Math.abs(col - column)
                if (diff < minDiff) {
                  minDiff = diff
                  bestIndex = index
                }
                index = lineText.indexOf(word, index + 1)
              }

              if (bestIndex !== -1) {
                targetColumn = bestIndex + 1
                foundSelection = {
                  startLineNumber: line,
                  startColumn: targetColumn,
                  endLineNumber: line,
                  endColumn: targetColumn + word.length,
                }
              }
            }

            const pos = { lineNumber: line, column: Math.max(1, targetColumn) }
            editor.setPosition(pos)
            editor.revealLineInCenter(line)

            if (foundSelection) {
              editor.setSelection(foundSelection)
            } else {
              const wordInfo = model.getWordAtPosition(pos)
              if (wordInfo) {
                editor.setSelection({
                  startLineNumber: line,
                  startColumn: wordInfo.startColumn,
                  endLineNumber: line,
                  endColumn: wordInfo.endColumn,
                })
              }
            }
          } else {
            editor.setPosition({ lineNumber: line, column: Math.max(1, column) })
            editor.revealLineInCenter(line)
          }
          editor.focus()
        } finally {
          if (inverseSyncTimeoutRef.current) clearTimeout(inverseSyncTimeoutRef.current)
          inverseSyncTimeoutRef.current = setTimeout(() => {
            isInverseSyncingRef.current = false
            inverseSyncTimeoutRef.current = null
          }, 100)
        }
      })

      featureDisposables.current.push(attachBeginEndMatching(editor, monaco))
      featureDisposables.current.push(attachDiagnostics(editor, monaco))

      const syncZoom = () => {
        const fontSize = editor.getOption(monaco.editor.EditorOption.fontSize)
        setZoomPercent(Math.max(1, Math.round((fontSize / BASE_EDITOR_FONT_SIZE) * 100)))
      }
      syncZoom()
      featureDisposables.current.push(
        editor.onDidChangeConfiguration((e) => {
          if (e.hasChanged(monaco.editor.EditorOption.fontSize)) syncZoom()
        }),
      )

      setEditorMountNonce((n) => n + 1)

      editor.focus()
    },
    [disposeLineCountListener, registerInverseSyncHandler, registerActiveMonacoEditor, setActiveModelPath, undoActiveEditor, redoActiveEditor],
  )

  useEffect(() => {
    return () => {
      synctexDebounceRef.current?.cancel()
      if (inverseSyncTimeoutRef.current) clearTimeout(inverseSyncTimeoutRef.current)
      registerInverseSyncHandler(null)
      registerActiveMonacoEditor(null)
      destroyMonacoBinding()
      setCollabBinding(null)
      disposeLineCountListener()
      for (const d of featureDisposables.current) d.dispose()
      featureDisposables.current = []
      editorRef.current = null
      setActiveEditor(null)
    }
  }, [destroyMonacoBinding, disposeLineCountListener, registerInverseSyncHandler, registerActiveMonacoEditor])

  useEffect(() => {
    synctexDebounceRef.current?.cancel()
  }, [fileId])

  // Fallback (no MonacoBinding): apply external/remote content without using a
  // controlled `value` prop, which would reset Monaco's undo stack every keystroke.
  useEffect(() => {
    if (!showFallback) return
    const ed = editorRef.current
    const model = ed?.getModel()
    if (!model || model.isDisposed()) return
    if (model.getValue() === content) return
    model.setValue(content)
  }, [content, showFallback, fileId])

  const isDark = appTheme.name === "dark"
  const isGray = appTheme.name === "gray"
  const filePath = resolveEditorFilePath(fileId)
  const isProjectSettingsFile = isProjectSettingsPath(filePath)
  const editorLanguage = isProjectSettingsFile ? "json" : LATEX_LANGUAGE_ID
  const theme = isProjectSettingsFile
    ? (isDark || isGray ? "vs-dark" : "vs")
    : (isGray ? "latex-gray" : isDark ? "latex-dark" : "latex-light")
  const scrollPercent = totalLines > 0 ? Math.round((cursorLine / totalLines) * 100) : 0
  const showCollaboratorCursors =
    hasAuthSession() && collabMode === "bound" && collabBinding !== null
  const presenceUsers = useFilePresenceUsers(
    getYjsBinding,
    filePath,
    state.crdtProviderEpoch,
  )
  const activeUserCount = presenceUsers.length
  const activeUsersLabel =
    activeUserCount === 1
      ? "1 User Actively Accessing This File"
      : `${activeUserCount} Users Actively Accessing This File`

  const monacoKey = `${state.sessionId ?? "pre"}`

  if (isImageFile(fileId) || currentFile?.isAsset) {
    return <ImagePreviewPanel node={node} fileId={fileId} />
  }

  return (
    <div className="flex flex-col h-full bg-[var(--ed-panel-bg)]">
      <div className="relative flex-1 overflow-hidden p-2">
        <Editor
          key={monacoKey}
          path={fileId}
          language={editorLanguage}
          {...(showFallback
            ? {
                // Uncontrolled after mount so Monaco keeps its undo stack.
                // Local typing: onChange → store; effect no-ops when model already matches.
                // External/remote updates: content differs → model.setValue below.
                defaultValue: content,
                onChange: (nextValue) => {
                  if (typeof nextValue === "string") {
                    updateContent(fileId, nextValue)
                  }
                },
              }
            : {
                defaultValue: content,
              })}
          theme={theme}
          beforeMount={handleBeforeMount}
          onMount={handleEditorMount}
          options={{
            readOnly,
            fontSize: 14,
            lineHeight: 22,
            fontFamily: "'Consolas', 'Courier New', 'monospace'",
            fontLigatures: false,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            renderLineHighlight: "line",
            lineNumbers: "on",
            lineNumbersMinChars: 3,
            glyphMargin: true,
            folding: true,
            foldingStrategy: "auto",
            automaticLayout: true,
            wordWrap: "on",
            wrappingIndent: "indent",
            tabSize: 2,
            insertSpaces: true,
            cursorBlinking: "smooth",
            cursorSmoothCaretAnimation: "on",
            smoothScrolling: true,
            padding: { top: 16, bottom: 16 },
            scrollbar: {
              vertical: "auto",
              horizontal: "auto",
              verticalScrollbarSize: 10,
              horizontalScrollbarSize: 10,
            },
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            overviewRulerBorder: false,
            contextmenu: true,
            mouseWheelZoom: true,
            bracketPairColorization: { enabled: false },
            guides: {
              indentation: false,
              bracketPairs: false,
            },
            renderWhitespace: "none",
            selectionHighlight: true,
            occurrencesHighlight: "off",
          }}
          loading={
            <div className="flex items-center justify-center h-full text-sm text-gray-400">
              Loading editor...
            </div>
          }
        />
        {showCollaboratorCursors && collabBinding ? (
          <CollaboratorCursors
            editor={activeEditor}
            fileId={fileId}
            awareness={collabBinding.awareness}
            doc={collabBinding.doc}
            ytext={collabBinding.ytext}
          />
        ) : null}
      </div>

      {synctexError && (
        <div className="shrink-0 px-3 py-1 bg-[var(--ed-status-error-bg)] border-t border-[var(--ed-status-error-border)] text-xs text-[var(--ed-status-error-text-strong)] flex items-center justify-between gap-2">
          <span className="truncate">SyncTeX: {synctexError}</span>
          <button
            onClick={() => setSynctexError(null)}
            className="shrink-0 text-red-400 hover:text-[var(--ed-status-error-dismiss-hover)]"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <div className="shrink-0 bg-transparent px-2 pb-2 pt-1.5 select-none">
        <div
          className={cn(
            "flex h-9 items-center justify-between gap-4 rounded-[10px] px-3.5",
            "border border-[var(--ed-breadcrumb-border)] bg-[var(--ed-breadcrumb-bg)]",
            "shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]",
          )}
        >
          <div className="flex min-w-0 items-center text-[12px] leading-none text-[var(--ed-breadcrumb-text)]">
            <span className="px-2.5 tabular-nums">{scrollPercent}%</span>
            <span className="h-3 w-px shrink-0 bg-[var(--ed-breadcrumb-divider)]" aria-hidden />
            <span className="px-2.5 tabular-nums">
              Line {cursorLine}/{totalLines}
            </span>
            <span className="h-3 w-px shrink-0 bg-[var(--ed-breadcrumb-divider)]" aria-hidden />
            <span className="px-2.5 tabular-nums">Zoom: {zoomPercent}%</span>
          </div>

          {activeUserCount > 0 && (
            <div className="flex shrink-0 items-center gap-2.5">
              <div className="flex items-center">
                {presenceUsers.map((user, index) => (
                  <Avatar
                    key={`${user.username || user.name}-${index}`}
                    className={cn("h-6 w-6 border-2 bg-white", index > 0 && "-ml-1.5")}
                    style={{ borderColor: user.color, zIndex: index + 1 }}
                    title={user.name}
                  >
                    {user.avatarUrl ? (
                      <AvatarImage src={user.avatarUrl} alt={user.name} />
                    ) : null}
                    <AvatarFallback
                      className="text-[10px] font-semibold"
                      style={{
                        backgroundColor: "#FFFFFF",
                        color: user.color,
                        border: `1px solid ${user.color}`,
                      }}
                    >
                      {(user.initials || user.name).slice(0, 1).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <span className="whitespace-nowrap text-[12px] leading-none text-[var(--ed-breadcrumb-muted-text)]">
                {activeUsersLabel}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
